import { Action, handleActions } from 'redux-actions'
import {
  CustomType,
  ProgressType,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../../services/api.types'
import {
  getCustomProgressSuccess,
  getDailyProgressSuccess,
  getProgressSuccess,
  setManualEntryPointsLogicId,
  setManualEntryProgressType,
  setProgressCustomType,
  setProgressPeriod,
  setProgressType,
} from './ProgressActions'
import { ProgressPeriod } from '../../../components/YumProgressPeriod/YumProgressPeriod'
import moment from 'moment'
import { PointsLogicId } from '../../ChallengeAdmin/components/ChallengePoints/ChallengePoints'

// TODO
export interface CustomProgressInnerState {
  [pointsLogicId: string]: UserCustomDailyProgress[]
}

export interface CustomProgressState {
  [goalId: string]: CustomProgressInnerState
}

export interface DailyProgressState {
  [goalId: string]: UserDailyDiaryProgress[]
}

class State {
  manualEntryProgressType: CustomType = CustomType.STEPS
  manualEntryPointsLogicId: PointsLogicId | null = null
  progressType: ProgressType = ProgressType.STEPS
  customType: CustomType = CustomType.EXERCISE_MIN
  progressPeriod: ProgressPeriod = ProgressPeriod.WEEK
  progressResults: UserDailyProgress[] = []
  customProgress: CustomProgressState = {}
  dailyProgress: DailyProgressState = {}
}

const convertKmToMi = (km: number) => {
  return km * 0.621371
}

const defaultCaloriesGoal = 2500
const defaultDistanceKmGoal = 8
const defaultDistanceMiGoal = 6

// Deprecated
const processProgressResults = (startDate: Date, endDate: Date, data: any, customStepsGoal?: number) => {
  const startDateMoment = moment.utc(startDate)
  const endDateMoment = moment.utc(endDate)

  const periodDays = endDateMoment.diff(startDateMoment, 'days') + 1

  // If any progress rows are missing, add placeholders.
  if (data.length < periodDays) {
    let [placeholderProgress] = data.slice(-1)
    if (!placeholderProgress) {
      const latestCaloriesGoal = localStorage.getItem('latestCaloriesGoal')
      placeholderProgress = {
        stepsGoal: customStepsGoal || 12000,
        caloriesGoal: latestCaloriesGoal ? parseInt(latestCaloriesGoal) : defaultCaloriesGoal,
        intensiveMinutesGoal: 20,
      }
    }

    const newData = []
    for (let day = moment(startDateMoment); day.isSameOrBefore(endDateMoment); day.add(1, 'days')) {
      const date = moment(day).locale('en').format('YYYY-MM-DD')
      const existingData = data.find((x: { date: string }) => x.date === date)
      const dayData = existingData || {
        ...placeholderProgress,
        stepsTotal: 0,
        caloriesTotal: 0,
        intensiveMinutesTotal: 0,
        totalPercentage: 0,
        integrationTotalPercentage: 0,
        distanceKm: 0,
        distanceMi: 0,
        date,
      }
      newData.push(dayData)
    }
    data = newData
  }
  data = data.map((row: any) => {
    const r = {
      ...row,
      stepsGoal: customStepsGoal || row.stepsGoal,
      totalGoal: 100,
      distanceKmGoal: defaultDistanceKmGoal,
      distanceMiGoal: defaultDistanceMiGoal,
      distanceMi: convertKmToMi(row.distanceKm),
    }
    if (r.caloriesGoal === null) {
      r.caloriesGoal = defaultCaloriesGoal
    }
    return r
  })
  return data
}

export const processCustomDiary = (startDate: string, endDate: string, data: UserCustomDailyProgress[]) => {
  const startDateMoment = moment(startDate)
  const endDateMoment = moment(endDate)
  const periodDays = endDateMoment.diff(startDateMoment, 'days') + 1
  // Guerrilla fix to some edge case bugs.
  //startDateMoment = moment(startDate).add(1, 'hours')
  if (data.length < periodDays) {
    const newData = [] as UserCustomDailyProgress[]
    for (let day = moment(startDateMoment); day.isSameOrBefore(endDateMoment); day.add(1, 'days')) {
      const date = day.locale('en').format('YYYY-MM-DD')
      const existingData = data.find((x: { date: string }) => x.date === date)
      newData.push(
        existingData || {
          date,
          value: 0,
        },
      )
    }
    data = newData
  }
  return data
}

export const processDailyDiary = (startDate: string, endDate: string, data: UserDailyDiaryProgress[]) => {
  const startDateMoment = moment(startDate)
  const endDateMoment = moment(endDate)
  const periodDays = endDateMoment.diff(startDateMoment, 'days') + 1
  if (!data || data.length < periodDays) {
    const newData = [] as UserDailyDiaryProgress[]
    for (let day = moment(startDateMoment); day.isSameOrBefore(endDateMoment); day.add(1, 'days')) {
      const date = day.locale('en').format('YYYY-MM-DD')
      const existingData = data?.find((x: { date: string }) => x.date === date)
      newData.push(
        existingData || {
          date,
          integrationValue: 0,
          manualValue: 0,
          sum: 0,
        },
      )
    }
    data = newData
  }
  return data
}

export const progress = handleActions<State, any>(
  {
    [String(setManualEntryProgressType)]: (
      state: State,
      { payload: manualEntryProgressType }: Action<CustomType>,
    ): State => ({
      ...state,
      manualEntryProgressType,
    }),
    [String(setManualEntryPointsLogicId)]: (
      state: State,
      { payload: manualEntryPointsLogicId }: Action<PointsLogicId>,
    ): State => ({
      ...state,
      manualEntryPointsLogicId,
    }),
    [String(setProgressType)]: (state: State, { payload: progressType }: Action<ProgressType>): State => ({
      ...state,
      progressType,
    }),
    [String(setProgressCustomType)]: (state: State, { payload: customType }: Action<CustomType>): State => ({
      ...state,
      customType,
    }),
    [String(setProgressPeriod)]: (state: State, { payload: progressPeriod }: Action<ProgressPeriod>): State => ({
      ...state,
      progressPeriod,
    }),
    [String(getProgressSuccess)]: (
      state: State,
      { payload: { startDate, endDate, progressResults, customStepsGoal } }: Action<any>,
    ): State => ({
      ...state,
      progressResults: processProgressResults(startDate, endDate, progressResults, customStepsGoal),
    }),
    // TODO: Get pointsLogic results...
    [String(getCustomProgressSuccess)]: (
      state: State,
      { payload: { goalId, startDate, endDate, customDiary, pointsLogicId } }: Action<any>,
    ): State => ({
      ...state,
      customProgress: {
        ...state.customProgress,
        [goalId]: {
          ...state.customProgress[goalId],
          [pointsLogicId || 'default']: processCustomDiary(startDate, endDate, customDiary),
        },
      },
      /*customProgress: {
        ...state.customProgress,
        [goalId]: processCustomDiary(startDate, endDate, customDiary),
      },*/
    }),
    [String(getDailyProgressSuccess)]: (
      state: State,
      { payload: { startDate, endDate, customType, dailyDiary } }: Action<any>,
    ): State => ({
      ...state,
      dailyProgress: {
        ...state.dailyProgress,
        [customType]: processDailyDiary(startDate, endDate, dailyDiary),
      },
    }),
  },
  new State(),
)

export { State as ProgressState }
